<template>
    <v-card v-if="thingSchema">
        <v-card-title>
            <v-row>
                <v-col cols="3" v-if="!editDisplayNameFormVisible">
                    {{ displayName }}
                    <v-icon-btn @click="showEditDisplayName"><v-icon>$edit</v-icon></v-icon-btn>
                </v-col>
                <v-col cols="2" v-if="editDisplayNameFormVisible">
                    <v-text-field density="compact" v-model="displayName"/>
                </v-col>
                <v-col cols="2" v-if="editDisplayNameFormVisible">
                    <v-btn @click="cancelEditDisplayName" color="warning">Cancel</v-btn>
                    <v-btn @click="updateDisplayName" color="primary" class="ml-5">Update</v-btn>
                </v-col>
                <v-col cols="6" v-if="isModified()">
                    <v-icon color="orange">$alert</v-icon> There are unsaved changes.
                    <v-btn color="orange" @click="seeSchemaChanges">See differences</v-btn>
                </v-col>
                <v-spacer/>
            </v-row>
        </v-card-title>
        <v-card-subtitle>
            <v-row>
                <v-col cols="3">
                    id: {{ thingSchema.id }}
                </v-col>
                <v-col>
                    name: {{ thingSchema.name }}
                </v-col>
            </v-row>
        </v-card-subtitle>
        <v-card-text>
            <v-row>
                <v-col cols="3">
                    <v-autocomplete label="Parent Schema"
                                    v-model="parentThingSchema"
                                    :items="parentThingSchemaOptions"
                                    return-object
                                    item-title="displayName"
                                    clearable />
                </v-col>
                <v-col cols="2">
                    <v-checkbox label="Sync entities to Benchling?" v-model="isBenchling"/>
                </v-col>
                <v-col cols="2">
                    <v-checkbox label="Geotaggable?" v-model="isGeotaggable"/>
                </v-col>
                <v-col cols="2">
                    <v-checkbox label="Is parent Benchling?" v-model="isParentBenchling"/>
                </v-col>
                <v-col cols="2">
                    <v-checkbox label="Has well position?" v-model="hasWellPosition"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <h3>One-Time Attributes</h3>
                </v-col>
                <v-spacer />
                <v-col v-if="userCanManageSchema" class="text-right">
                    <v-btn color="primary" @click="showAddOneTimeAttributeForm">Add Attribute</v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <schema-attributes-list :attributes="oneTimeAttributes"
                                            :is-registry-schema="false"
                                            :allow-update="true"
                                            :is-template-schema="isTemplate"
                                            :schema-changes="schemaChanges"
                                            attribute-type="one-time"
                                            @showEditAttributeForm="showEditOneTimeAttributeForm"
                                            @deleteAttribute="deleteAttribute"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <h3>Time Series Attributes</h3>
                </v-col>
                <v-spacer />
                <v-col v-if="userCanManageSchema" class="text-right">
                    <v-btn color="primary" @click="showAddTimeSeriesAttributeForm">Add Attribute</v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <schema-attributes-list :attributes="timeSeriesAttributes"
                                            :is-registry-schema="false"
                                            :allow-update="true"
                                            :is-template-schema="isTemplate"
                                            :schema-changes="schemaChanges"
                                            attribute-type="time-series"
                                            @showEditAttributeForm="showEditTimeSeriesAttributeForm"
                                            @deleteAttribute="deleteAttribute"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <h3>Setup Configuration</h3>
                </v-col>
                <v-col class="text-right">
                    <v-btn color="primary" @click="showAddSetupAttributeForm">Add Setup Attribute</v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <v-text-field label="Naming template" v-model="namingTemplate"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    Configuration specifying how many {{ thingSchema.displayName }} instances to create during experiment setup:
                </v-col>
            </v-row>
            <v-row v-for="setupPerConfig of setupPerConfigs">
                <v-col cols="6">
                    <v-text-field v-if="isNumericSetupAttribute(setupPerConfig)" :label="setupPerConfig.attributeName" v-model="setupPerConfig.value"/>
                    <span v-else>Number of associated <b>{{ setupPerConfig.attributeName }}</b> entry relation entities</span>
                </v-col>
                <v-col>
                    <v-icon-btn @click="removeSetupPerConfig(setupPerConfig)"><v-icon>$trash-can-outline</v-icon></v-icon-btn>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="showConfirmReset" color="warning">Reset</v-btn>
            <v-btn @click="save" color="primary">Save</v-btn>
        </v-card-actions>

        <v-dialog v-if="editAttribute" v-model="editAttributeFormVisible" persistent>
            <schema-attribute-form
                :all-registry-schemas="allRegistrySchemas"
                :attribute="editAttribute"
                :thing-schema="thingSchema"
                :isNewAttribute="isNewAttribute"
                :is-registry-schema="false"
                :all-thing-schemas="allThingSchemas"
                @closeEditAttributeForm="closeEditAttributeForm"
                @saveAttribute="saveAttribute"
            />
        </v-dialog>

        <v-dialog v-model="addSetupAttributeFormVisible" width="50%">
            <v-card>
                <v-card-title>Select setup configuration attribute</v-card-title>
                <v-card-subtitle>
                    Used to configure how many {{ thingSchema.displayName }} entities to create.
                    <br/>
                    Attributes must be part of the naming template {{ namingTemplate }}
                </v-card-subtitle>
                <v-card-text>
                    <v-alert v-if="isSetupPerConfigFullyConfigured" color="warning">
                        All naming template attributes are configured
                    </v-alert>
                    <v-autocomplete v-else
                              v-model="selectedSetupAttribute"
                              :items="namingTemplateAttributes"
                              item-title="display_name"
                              return-object
                    ></v-autocomplete>
                </v-card-text>
                <v-card-actions>
                    <v-btn @click="cancelAddSetupAttribute" color="primary">Cancel</v-btn>
                    <v-btn @click="addSetupAttribute" :disabled="!validAddSetupAttribute" color="primary">Add</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="schemaChangesDialogVisible" width="50%">
            <SchemaChanges :thing-schema="thingSchema"
                           :schema-changes="schemaChanges"
                           @close="closeSchemaChangesDialog"/>
        </v-dialog>
    </v-card>
</template>
<script setup lang="ts">
import { ref, watch, computed, Ref } from "vue";
import { useToast } from "vue-toastification";
import _ from "lodash";
import {
    Attribute,
    SetupPerConfig,
    ThingSchema,
} from "@/services/things/models";
import {
    isRelationAttribute,
    sortAttributesFn,
} from "@/lib/thingUtils";
import SchemaAttributesList from "./SchemaAttributesList.vue";
import SchemaAttributeForm from "@/components/SchemaConfig/SchemaAttributeForm.vue";
import { useSchemaAttributeForm } from "@/components/SchemaConfig/schemaAttributeFormComposable";
import { useAuth } from "@/lib/ts/auth";
import {
    buildAttributeRecordObjectToSaveFromList,
    buildSetupPerConfigObject,
    findThingSchemaByName,
    getProtocolLevelAttributes,
    getSchemaChanges,
    parseNamingTemplate,
    parseNamingTemplateAttributes,
    parseSetupPerConfigs,
} from "@/lib/schemaUtils";
import { removeFromArrayRef, upsertInArrayRef } from "@/lib/arrayUtils";
import {
    manageSchemasAPI,
    ThingSchemaAdminConfig,
    ThingSchemaMutationData,
} from "@/services/schemas/manageSchemasAPI";
import { withLoadingScreen } from "@/lib/loading";
import { transformEmptyStringToNull } from "@/lib/utils";
import SchemaChanges from "@/components/SchemaConfig/SchemaChanges.vue";
import { useStore } from "vuex";

const store = useStore();
const auth = useAuth();
const toast = useToast();

const {
    allRegistrySchemas,
    editAttribute,
    editAttributeFormVisible,
    isNewAttribute,
    showAddAttributeForm,
    showEditAttributeForm,
    closeEditAttributeForm,
    initialize
} = useSchemaAttributeForm()

const { allThingSchemas, thingSchema } = defineProps<{
    allThingSchemas: ThingSchema[],
    thingSchema: ThingSchemaAdminConfig
}>();

const emit = defineEmits<{
    (e: "thingSchemaSaved");
}>()

const displayName = ref("");
const oneTimeAttributes = ref<Attribute[]>([]);
const timeSeriesAttributes = ref<Attribute[]>([]);
const parentThingSchemaOptions = ref<ThingSchema[]>([]);
const parentThingSchema = ref<ThingSchema>();
const isBenchling = ref(false);
const isGeotaggable = ref(false);
const isParentBenchling = ref(false);
const hasWellPosition = ref(false);
const addSetupAttributeFormVisible = ref(false);
const setupPerConfigs = ref<SetupPerConfig[]>([]);
const namingTemplate = ref("");
const selectedSetupAttribute = ref<Attribute>();
const editDisplayNameFormVisible = ref(false);
const isTemplate = ref(false);
const schemaChangesDialogVisible = ref(false);

let selectedAttributeType: "one-time" | "time-series" = "one-time";

watch(() => thingSchema, () => {
    resetFormState();
}, { immediate: true });

const allNamingTemplateAttributes = computed(() => {
    return parseNamingTemplateAttributes(thingSchema, namingTemplate.value);
});

const namingTemplateAttributes = computed(() => {
    return allNamingTemplateAttributes.value.filter((attr) => {
        return !isNamingAttributeConfigured(attr.name);
    })
})

const isSetupPerConfigFullyConfigured = computed(() => {
    return allNamingTemplateAttributes.value.length > 0 && namingTemplateAttributes.value.length === 0;
});

const userCanManageSchema = computed(() => {
    return auth.isAuthorizedForOneOf("Protocols:Create", "Protocols:Update", "SchemaConfigs:Manage");
});

const validAddSetupAttribute = computed(() => {
    return !_.isNil(selectedSetupAttribute.value);
});

const schemaChanges = computed(() => {
    return getSchemaChanges(
        thingSchema,
        displayName.value,
        parentThingSchema.value || null,
        isBenchling.value,
        isGeotaggable.value,
        isParentBenchling.value,
        hasWellPosition.value,
        setupPerConfigs.value,
        namingTemplate.value,
        oneTimeAttributes.value,
        timeSeriesAttributes.value
    );
});

watch(schemaChanges, (schemaChanges) => {
    if (schemaChanges.isModified) {
        store.commit("setNavigationWarning");
    } else {
        store.commit("clearNavigationWarning");
    }
});

/**
 * Sets the ThingSchema form state to the current thingSchema. Used when the
 * ThingSchema propertyForDisplay is updated or when resetting the form.
 *
 * This is a bit more straight forward than mutating the ThingSchema, then
 * having to have a clone of it to copy properties back to reset the form.
 */
function resetFormState() {
    displayName.value = thingSchema.displayName;
    // Note: ensure this doesn't modify the original attribute instances on the schema
    oneTimeAttributes.value = _.cloneDeep(thingSchema.oneTimeAttributeConfigurations);
    timeSeriesAttributes.value = _.cloneDeep(thingSchema.timeSeriesAttributeConfigurations);
    parentThingSchemaOptions.value = buildParentThingSchemaOptions();
    parentThingSchema.value = findThingSchemaByName(allThingSchemas, thingSchema.parentThingSchemaName);
    isBenchling.value = thingSchema.isBenchling;
    isGeotaggable.value = thingSchema.isGeotaggable;
    isParentBenchling.value = thingSchema.isParentBenchling;
    hasWellPosition.value = thingSchema.hasWellPosition;
    setupPerConfigs.value = parseSetupPerConfigs(thingSchema);
    namingTemplate.value = parseNamingTemplate(thingSchema);

    oneTimeAttributes.value.sort(sortAttributesFn);
    timeSeriesAttributes.value.sort(sortAttributesFn);

    isTemplate.value = thingSchema.protocolCode === null;
}

function buildParentThingSchemaOptions(): ThingSchema[] {
    return allThingSchemas.filter((parentThingSchemaOption) => {
        return parentThingSchemaOption.name !== thingSchema.name;
    });
}

function showEditDisplayName() {
    editDisplayNameFormVisible.value = true;
}

function cancelEditDisplayName() {
    editDisplayNameFormVisible.value = false;
    displayName.value = thingSchema.displayName;
}

function updateDisplayName() {
    editDisplayNameFormVisible.value = false;
}

function showEditOneTimeAttributeForm(attribute: Attribute) {
    selectedAttributeType = "one-time";
    showEditAttributeForm(attribute);
}

function showEditTimeSeriesAttributeForm(attribute: Attribute) {
    selectedAttributeType = "time-series";
    showEditAttributeForm(attribute);
}

function showAddOneTimeAttributeForm() {
    selectedAttributeType = "one-time";
    showAddAttributeForm();
}

function showAddTimeSeriesAttributeForm() {
    selectedAttributeType = "time-series";
    showAddAttributeForm();
}

function isNamingAttributeConfigured(attributeName: string): boolean {
    const setupPerConfigForAttr = getSetupPerConfigForAttributeName(attributeName);
    return !_.isNil(setupPerConfigForAttr);
}

function getSetupPerConfigForAttributeName(attributeName: string): SetupPerConfig | undefined {
    return setupPerConfigs.value.find((setupPerConfig) => {
        return setupPerConfig.attributeName === attributeName;
    });
}

function isNumericSetupAttribute(setupPerConfig: SetupPerConfig): boolean {
    const attributeName = setupPerConfig.attributeName;
    const attribute = oneTimeAttributes.value.find((attr) => attr.name === attributeName);
    return !_.isNil(attribute) && attribute.type === "number";
}

function removeSetupPerConfig(setupPerConfig: SetupPerConfig) {
    removeFromArrayRef(setupPerConfigs, setupPerConfig);
}

function showAddSetupAttributeForm() {
    addSetupAttributeFormVisible.value = true;
}

function cancelAddSetupAttribute() {
    addSetupAttributeFormVisible.value = false;
}

function addSetupAttribute() {
    const attribute = selectedSetupAttribute.value;
    addSetupAttributeFormVisible.value = false;
    if (_.isNil(attribute)) {
        return;
    }

    if (isRelationAttribute(attribute)) {
        setupPerConfigs.value.push({
            attributeName: attribute.name,
            value: attribute.relation as string
        });
    } else {
        setupPerConfigs.value.push({
            attributeName: attribute.name,
            value: "1"
        });
    }
}

async function deleteAttribute(attribute: Attribute, attributeType: "one-time" | "time-series" | null) {
    const attributes = attributeType === "one-time"? oneTimeAttributes : timeSeriesAttributes;
    // replace with template if there's a template.
    if (attribute.source === "overridden") {
        // Attribute exists on the template with an override on the local protocol schema. Remove
        // the local protocol Attribute and replace it with the template Attribute.
        const templateAttribute = getTemplateAttributeByNameAndType(attribute.name, selectedAttributeType);
        if (templateAttribute) {
            upsertInArrayRef(attributes, templateAttribute, (attr: Attribute) => attr.name === attribute.name);
        } else {
            // If the source is "overridden" but we didn't find the attribute in the template, something has gone wrong.
            toast.error(`Attribute ${ attribute.name } has source of ${ attribute.source } but did not find attribute in the template schema!`);
        }
    } else if (isTemplate.value || attribute.source === "protocol") {
        // Attribute only exists on the local protocol schema, it can be removed from the list.
        removeFromArrayRef(attributes, attribute, (attr: Attribute) => attr.name === attribute.name);
    }
}

function getTemplateAttributeByNameAndType(
    attributeName: string,
    attributeType: "one-time" | "time-series"
): Attribute | undefined {
    const attributes = attributeType === "one-time"
        ? thingSchema.templateOneTimeAttributeConfigurations : thingSchema.templateTimeSeriesAttributeConfigurations;
    return attributes.find((templateAttribute) => templateAttribute.name === attributeName);
}

function saveAttribute(attribute: Attribute) {
    const attributesRef = selectedAttributeType === "one-time" ? oneTimeAttributes : timeSeriesAttributes;
    upsertAttribute(attribute, attributesRef);
    closeEditAttributeForm();
}

function upsertAttribute(attribute: Attribute, attributesRef: Ref<Attribute[]>) {
    if (isExistingAttribute(attribute, attributesRef.value)) {
        // Note: it is critical to set these here, so that `save()` will filter the list of all
        // attributes to the set of protocol-level attributes based on their `source`. `template`
        // attributes will be filtered out of a protocol-level schema.
        const source = isTemplate.value ? "template" : "overridden";
        upsertInArrayRef(
            attributesRef,
            {
                ...attribute,
                source,
            },
            (attr: Attribute) => attr.name === attribute.name);
    } else {
        const source = isTemplate.value ? "template" : "protocol";
        attributesRef.value.push({
            ...attribute,
            source,
        });
    }
    attributesRef.value.sort(sortAttributesFn);
}

function isExistingAttribute(attribute: Attribute, existingAttributes: Attribute[]): boolean {
    return !_.isNil(existingAttributes.find((existingAttribute) => existingAttribute.name === attribute.name));
}

function showConfirmReset() {
    if (confirm("Are you sure you want to discard your changes?")) {
        resetFormState();
    }
}

async function save() {
    const setupPerConfigObject = buildSetupPerConfigObject(setupPerConfigs.value);

    let oneTimeAttributesToSave: Attribute[];
    let timeSeriesAttributesToSave: Attribute[];

    if (isTemplate.value) {
        oneTimeAttributesToSave = oneTimeAttributes.value;
        timeSeriesAttributesToSave = timeSeriesAttributes.value;
    } else {
        oneTimeAttributesToSave = getProtocolLevelAttributes(oneTimeAttributes.value);
        timeSeriesAttributesToSave = getProtocolLevelAttributes(timeSeriesAttributes.value);
    }

    const oneTimeAttributesObjectToSave = buildAttributeRecordObjectToSaveFromList(oneTimeAttributesToSave);
    const timeSeriesAttributesObjectToSave = buildAttributeRecordObjectToSaveFromList(timeSeriesAttributesToSave);

    const manageThingSchemaData: ThingSchemaMutationData = {
        name: thingSchema.name,
        oneTimeAttributes: oneTimeAttributesObjectToSave,
        timeSeriesAttributes: timeSeriesAttributesObjectToSave,
        parentThingSchemaName: parentThingSchema.value?.name || "entry",
        displayName: displayName.value,
        isBenchling: isBenchling.value,
        isGeotaggable: isGeotaggable.value,
        isParentBenchling: isParentBenchling.value,
        hasWellPosition: hasWellPosition.value,
        canAddInstanceInMango: thingSchema.canAddInstanceInMango,
        setup: {
            naming_template: transformEmptyStringToNull(namingTemplate.value) || undefined,
            per: setupPerConfigObject
        }
    };

    console.log(manageThingSchemaData);

    if (confirm(`Are you sure you want to save your changes to ${thingSchema.displayName}?`)) {
        await withLoadingScreen(async () => {
            await manageSchemasAPI.updateThingSchema(thingSchema.id, manageThingSchemaData);
            toast.success(`${manageThingSchemaData.displayName} Updated`);
            emit("thingSchemaSaved");
        });
    }
}

function isModified() {
    return schemaChanges.value.isModified;
}

function seeSchemaChanges() {
    schemaChangesDialogVisible.value = true;
}

function closeSchemaChangesDialog() {
    schemaChangesDialogVisible.value = false;
}

initialize();

</script>
<style>
.modified {
    background-color: #ffae00;
}
</style>
